import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Camera, X, Check, Loader2, Plus, Upload, RotateCcw, SkipForward, Smartphone } from "lucide-react";
import { FOTO_SLOTS } from "@/lib/simulator";
import { supabase } from "@/lib/supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

import exemploFrente from "@/assets/foto-exemplo/frente.jpg";
import exemploTraseira from "@/assets/foto-exemplo/traseira.jpg";
import exemploDiagFrontal from "@/assets/foto-exemplo/diagonal_frontal.jpg";
import exemploDiagFrontalLado2 from "@/assets/foto-exemplo/diagonal_frontal_lado2.jpg";
import exemploDiagTrasLado1 from "@/assets/foto-exemplo/diagonal_traseira_lado1.jpg";
import exemploDiagTrasLado2 from "@/assets/foto-exemplo/diagonal_traseira_lado2.jpg";
import exemploPainel from "@/assets/foto-exemplo/painel.jpg";
import exemploMotor from "@/assets/foto-exemplo/motor.jpg";
import exemploPortaMalas from "@/assets/foto-exemplo/Porta-Mala.jpg";

const EXEMPLO_MAP: Record<string, string> = {
  frente: exemploFrente,
  traseira: exemploTraseira,
  lateral_esq: exemploDiagFrontal,
  lateral_dir: exemploDiagFrontalLado2,
  diag_tras_esq: exemploDiagTrasLado1,
  diag_tras_dir: exemploDiagTrasLado2,
  painel: exemploPainel,
  motor: exemploMotor,
  portamalas: exemploPortaMalas,
};

interface Props {
  leadId: string;
  onNext: (files: File[], urls: string[]) => void;
  onBack: () => void;
}

const CAPTURE_SLOTS = FOTO_SLOTS.filter((s) => s.id !== "extras");

export function StepFotos({ leadId, onNext, onBack }: Props) {
  const [photos, setPhotos] = useState<Record<string, { file: File; preview: string }>>({});
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<"upload" | "camera">("upload");
  const [cameraStep, setCameraStep] = useState(0);
  const [extraPhotos, setExtraPhotos] = useState<Array<{ id: string; file: File; preview: string }>>([]);
  const [extraCounter, setExtraCounter] = useState(0);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const extraInputRef = useRef<HTMLInputElement | null>(null);

  const requiredSlots = FOTO_SLOTS.filter((s) => s.id !== "extras");
  const filledCount = requiredSlots.filter((s) => photos[s.id]).length;
  const hasPhotos = filledCount > 0 || extraPhotos.length > 0;

  const handleFile = (slotId: string, file: File) => {
    const preview = URL.createObjectURL(file);
    setPhotos((prev) => ({ ...prev, [slotId]: { file, preview } }));
  };

  const removePhoto = (slotId: string) => {
    setPhotos((prev) => {
      const copy = { ...prev };
      if (copy[slotId]) URL.revokeObjectURL(copy[slotId].preview);
      delete copy[slotId];
      return copy;
    });
  };

  const handleExtraFile = (file: File) => {
    const id = `extra_${Date.now()}`;
    const preview = URL.createObjectURL(file);
    setExtraPhotos((prev) => [...prev, { id, file, preview }]);
    setExtraCounter((prev) => prev + 1);
  };

  const removeExtraPhoto = (id: string) => {
    setExtraPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found) URL.revokeObjectURL(found.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleUploadAll = async () => {
    setUploading(true);
    try {
      const entries = Object.entries(photos);
      // Include extra photos
      const allEntries = [
        ...entries,
        ...extraPhotos.map((ep) => [ep.id, { file: ep.file, preview: ep.preview }] as const),
      ];
      const urls: string[] = [];
      const files: File[] = [];

      for (const [slotId, { file }] of allEntries) {
        const path = `${leadId}/${Date.now()}_${slotId}_${file.name}`;
        const { error } = await supabase.storage
          .from("vehicle-photos")
          .upload(path, file);

        if (error) throw error;

        const { data: urlData } = supabase.storage
          .from("vehicle-photos")
          .getPublicUrl(path);

        urls.push(urlData.publicUrl);
        files.push(file);
      }

      onNext(files, urls);
    } catch {
      alert("Erro ao enviar fotos. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  // Camera mode helpers
  const currentSlot = CAPTURE_SLOTS[cameraStep];
  const currentPhoto = currentSlot ? photos[currentSlot.id] : null;

  const goToNextCameraStep = () => {
    let next = cameraStep + 1;
    // Skip slots that already have photos
    while (next < CAPTURE_SLOTS.length && photos[CAPTURE_SLOTS[next].id]) {
      next++;
    }
    if (next >= CAPTURE_SLOTS.length) {
      // All done, show summary
      setCameraStep(CAPTURE_SLOTS.length);
    } else {
      setCameraStep(next);
    }
  };

  const handleCameraCapture = (file: File) => {
    if (currentSlot) {
      handleFile(currentSlot.id, file);
    }
  };

  const ScannerLine = () => (
    <motion.div
      initial={{ top: "0%" }}
      animate={{ top: "100%" }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      className="absolute left-0 right-0 h-0.5 bg-primary/40 shadow-[0_0_10px_rgba(var(--primary),0.5)] z-20 pointer-events-none"
    />
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="space-y-2">
        <h2 className="text-3xl font-heading font-black uppercase tracking-tighter">Perícia Digital</h2>
        <p className="text-muted-foreground text-lg font-medium">
          Envie fotos reais para detecção computadorizada de danos.
        </p>
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest mt-2">
          <span className={cn(hasPhotos ? "text-primary" : "text-muted-foreground")}>
            Status: {filledCount}/{requiredSlots.length} Camadas Analisadas
          </span>
          {hasPhotos && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
        </div>
      </div>

      {/* Mode Selector */}
      <div className="flex gap-2 p-1 rounded-lg bg-muted/50 border border-border/50">
        <button
          onClick={() => setMode("upload")}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-xs font-black uppercase tracking-widest transition-all",
            mode === "upload"
              ? "bg-background text-foreground shadow-sm border border-border/50"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Upload className="w-4 h-4" />
          Importar Arquivos
        </button>
        <button
          onClick={() => {
            setMode("camera");
            const firstEmpty = CAPTURE_SLOTS.findIndex((s) => !photos[s.id]);
            setCameraStep(firstEmpty >= 0 ? firstEmpty : CAPTURE_SLOTS.length);
          }}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-xs font-black uppercase tracking-widest transition-all",
            mode === "camera"
              ? "bg-background text-foreground shadow-sm border border-border/50"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Camera className="w-4 h-4" />
          Câmera Direta
        </button>
      </div>

      {/* Skip button */}
      <Button
        variant="link"
        className="w-full text-muted-foreground text-xs uppercase font-bold tracking-widest hover:text-primary transition-colors"
        onClick={() => onNext([], [])}
      >
        <SkipForward className="w-4 h-4 mr-2" />
        Pular para análise parcial
      </Button>

      <AnimatePresence mode="wait">
        {mode === "upload" ? (
          <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {CAPTURE_SLOTS.map((slot) => {
                const photo = photos[slot.id];
                const exemploSrc = EXEMPLO_MAP[slot.id];
                return (
                  <div key={slot.id} className="relative flex flex-col gap-2 group">
                    <input
                      ref={(el) => { inputRefs.current[slot.id] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFile(slot.id, f);
                      }}
                    />

                    <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60 group-hover:text-primary transition-colors">
                      {slot.label}
                    </span>

                    <button
                      onClick={() => inputRefs.current[slot.id]?.click()}
                      className={cn(
                        "w-full aspect-[4/3] border-2 transition-all overflow-hidden relative",
                        photo
                          ? "border-primary bg-primary/5 shadow-[0_0_15px_rgba(var(--primary),0.2)]"
                          : "border-border hover:border-primary/50 bg-background"
                      )}
                    >
                      {photo ? (
                        <>
                          <img src={photo.preview} alt={slot.label} className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700" />
                          <ScannerLine />
                        </>
                      ) : exemploSrc ? (
                        <>
                          <img src={exemploSrc} alt={`Exemplo ${slot.label}`} className="absolute inset-0 w-full h-full object-cover opacity-20 filter grayscale" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Plus className="w-6 h-6 text-muted-foreground/50 group-hover:text-primary transition-colors" />
                          </div>
                        </>
                      ) : (
                        <Camera className="w-6 h-6 text-muted-foreground" />
                      )}
                    </button>

                    {photo && (
                      <button
                        onClick={() => removePhoto(slot.id)}
                        className="absolute top-6 right-1 w-6 h-6 bg-foreground text-background border border-border flex items-center justify-center z-30 hover:bg-destructive hover:text-white transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Dynamic Extra Photos */}
              {extraPhotos.map((photo) => (
                <div key={photo.id} className="relative flex flex-col gap-2 group">
                  <span className="text-[10px] font-black uppercase tracking-widest text-primary">
                    Extra
                  </span>
                  <div className="w-full aspect-[4/3] border-2 border-primary bg-primary/5 shadow-[0_0_15px_rgba(var(--primary),0.2)] overflow-hidden relative">
                    <img src={photo.preview} alt="Extra" className="w-full h-full object-cover" />
                    <ScannerLine />
                  </div>
                  <button
                    onClick={() => removeExtraPhoto(photo.id)}
                    className="absolute top-6 right-1 w-6 h-6 bg-foreground text-background border border-border flex items-center justify-center z-30 hover:bg-destructive hover:text-white transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {/* Extra Photo Slot Trigger */}
              <div className="relative flex flex-col gap-2 group">
                <input
                  ref={extraInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleExtraFile(f);
                  }}
                />
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/40 italic">
                  Adicionar mais...
                </span>
                <button
                  onClick={() => extraInputRef.current?.click()}
                  className="w-full aspect-[4/3] border-2 border-dashed border-border hover:border-primary/50 bg-background flex flex-col items-center justify-center gap-2 group transition-all"
                >
                  <Plus className="w-6 h-6 text-muted-foreground/50 group-hover:text-primary transition-colors" />
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="camera" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Camera code as is but with updated styling for summary */}
            <AnimatePresence mode="wait">
              {cameraStep < CAPTURE_SLOTS.length && currentSlot ? (
                <motion.div key={cameraStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  {/* ... camera capture UI ... */}
                  <div className="relative rounded-xl overflow-hidden border aspect-[4/3] max-w-md mx-auto bg-muted">
                    {currentPhoto ? (
                      <>
                        <img src={currentPhoto.preview} alt={currentSlot.label} className="w-full h-full object-cover" />
                        <ScannerLine />
                      </>
                    ) : (
                      EXEMPLO_MAP[currentSlot.id] && (
                        <img src={EXEMPLO_MAP[currentSlot.id]} alt="Exemplo" className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale" />
                      )
                    )}
                  </div>
                  <div className="max-w-md mx-auto space-y-3">
                    <Button size="xl" className="w-full font-black uppercase tracking-widest" onClick={() => cameraInputRef.current?.click()}>
                      {currentPhoto ? "Refazer Captura" : "Iniciar Scanner"}
                    </Button>
                    {currentPhoto && (
                      <Button variant="outline" size="xl" className="w-full font-black uppercase tracking-widest" onClick={goToNextCameraStep}>
                        Confirmar Diagnóstico
                      </Button>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div key="summary" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                  <div className="grid grid-cols-3 gap-3">
                    {CAPTURE_SLOTS.map((slot) => {
                      const photo = photos[slot.id];
                      return (
                        <div key={slot.id} className="relative aspect-[4/3] border border-border/50 bg-muted/20 overflow-hidden">
                          {photo ? (
                            <>
                              <img src={photo.preview} alt={slot.label} className="w-full h-full object-cover" />
                              <ScannerLine />
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <Camera className="w-4 h-4 text-muted-foreground/30" />
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/60 text-[8px] text-white font-black uppercase tracking-tighter text-center">
                            {slot.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-4 pt-4">
        <Button variant="outline" size="xl" onClick={onBack} className="flex-1 font-black uppercase tracking-widest">
          Descartar
        </Button>
        <Button
          onClick={hasPhotos ? handleUploadAll : () => onNext([], [])}
          disabled={uploading}
          size="xl"
          className="flex-1 group font-black uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)]"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analisando...</>
          ) : (
            <>
              Confirmar
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
}
